import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { Button } from 'src/components/Button';
import { Select } from 'src/components/Select';
import { ConfirmModal } from 'src/components/ConfirmModal';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { ContentSkeleton } from 'src/components/ContentSkeleton';
import { useDeferredLoading } from 'src/hooks/useDeferredLoading';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import {
  CaseDetailResponse,
  CaseOutcome,
  fetchComplianceCase,
  formatCaseReasonLabel,
  formatCaseUserName,
  formatOpenFor,
  formatOutcomeLabel,
  rescreenDeposit,
  resolveComplianceCase,
  whitelistCaseAddresses,
} from 'src/api/adminCompliance';
import { formatUserStatusLabel } from 'src/api/adminUsers';
import { convertDate, formatFieldString } from 'src/components/Table/helpers';
import { ROUTE_USERS } from 'src/utils/routes';

import CopyableValue from './CopyableValue';

import {
  ActionRow,
  ActivityItem,
  ActivityList,
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  NotesInput,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from './styled';

type CaseDetailsModalProps = {
  caseNumber: number;
  onClose: () => void;
  onResolved?: () => void;
};

const CaseDetailsModal: React.FC<CaseDetailsModalProps> = ({ caseNumber, onClose, onResolved }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<CaseDetailResponse | null>(null);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [whitelistPrompt, setWhitelistPrompt] = useState(false);
  const [rescreening, setRescreening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchComplianceCase(caseNumber);
      setDetail(res);
      const first = res.available_outcomes?.[0] ?? '';
      setOutcome(first);
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load case',
          isSuccess: false,
        }),
      );
      onClose();
    } finally {
      setLoading(false);
    }
  }, [caseNumber, dispatch, onClose]);

  useEffect(() => {
    load();
  }, [load]);

  const complianceCase = detail?.compliance_case;
  const isOpen = String(complianceCase?.status || '').toUpperCase() === 'OPEN';
  const user = detail?.user ?? complianceCase?.user;
  const transaction = detail?.transaction ?? complianceCase?.transaction;

  // Without this the skeleton is unmounted the moment the request resolves —
  // on a fast connection it never becomes visible.
  const showSkeleton = useDeferredLoading(loading || !complianceCase, Boolean(complianceCase));

  const outcomeOptions = (detail?.available_outcomes || []).map((value) => ({
    value,
    label: formatOutcomeLabel(value),
  }));

  // A SCREENING_FAILED case cannot be resolved until the screening is retried:
  // the API refuses the resolution, so this is the way out of that queue.
  const isScreeningFailed = String(complianceCase?.reason || '').toUpperCase() === 'SCREENING_FAILED';

  const handleRescreen = async () => {
    if (!transaction?.id || !isOpen) return;
    setRescreening(true);
    try {
      const result = await rescreenDeposit(transaction.id);
      const outcomeText = formatFieldString(String(result.outcome || 'done'));
      dispatch(
        setAppAlert({
          message: result.deposit?.credited
            ? `Screening passed — deposit credited (${outcomeText})`
            : `Screening retried: ${outcomeText}${result.reason ? ` — ${result.reason}` : ''}`,
          isSuccess: true,
        }),
      );
      if (result.deposit?.credited) {
        onResolved?.();
        return;
      }
      await load();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? e?.response?.data?.message ?? 'Failed to retry screening',
          isSuccess: false,
        }),
      );
    } finally {
      setRescreening(false);
    }
  };

  const handleResolve = async () => {
    if (!outcome || !isOpen) return;
    setSaving(true);
    try {
      await resolveComplianceCase(caseNumber, {
        outcome: outcome as CaseOutcome,
        notes: notes.trim() || undefined,
        restrict_account: outcome === 'CONFIRMED' ? true : undefined,
      });
      dispatch(setAppAlert({ message: 'Case resolved', isSuccess: true }));
      if (outcome === 'CLEARED') {
        setWhitelistPrompt(true);
        return;
      }
      onResolved?.();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? e?.response?.data?.message ?? 'Failed to resolve case',
          isSuccess: false,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleWhitelist = async () => {
    try {
      await whitelistCaseAddresses(caseNumber);
      dispatch(setAppAlert({ message: 'Address whitelisted', isSuccess: true }));
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to whitelist address',
          isSuccess: false,
        }),
      );
    } finally {
      setWhitelistPrompt(false);
      onResolved?.();
    }
  };

  return (
    <>
      <Modal isOpen onClose={onClose}>
        {showSkeleton || !complianceCase ? (
          <ContentSkeleton variant="detailModal" count={4} />
        ) : (
          <>
            <Breadcrumb>Transactions / Cases / #{complianceCase.case_number}</Breadcrumb>
            <HeadlineRow>
              <TitleGroup>
                <Title>
                  Case #{complianceCase.case_number}
                  <StatusBadge $status={String(complianceCase.status)}>
                    {formatFieldString(String(complianceCase.status))}
                  </StatusBadge>
                  {complianceCase.risk_level ? (
                    <StatusBadge $status={String(complianceCase.risk_level)}>
                      {formatFieldString(String(complianceCase.risk_level))}
                    </StatusBadge>
                  ) : null}
                </Title>
                <MetaLine>
                  Opened {complianceCase.opened_at ? convertDate(String(complianceCase.opened_at)).date : '—'}
                  {' · '}
                  Open for {formatOpenFor(complianceCase.open_for_ms)}
                </MetaLine>
              </TitleGroup>
            </HeadlineRow>

            <Card>
              <CardTitle>Case</CardTitle>
              <DetailGrid>
                <DetailItem>
                  <DetailLabel>Reason</DetailLabel>
                  <DetailValue>
                    {formatCaseReasonLabel(complianceCase.reason, complianceCase.reason_detail)}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Outcome</DetailLabel>
                  <DetailValue $muted={!complianceCase.outcome}>
                    {formatOutcomeLabel(complianceCase.outcome ?? undefined)}
                  </DetailValue>
                </DetailItem>
                {complianceCase.resolution_notes ? (
                  <DetailItem>
                    <DetailLabel>Notes</DetailLabel>
                    <DetailValue>{complianceCase.resolution_notes}</DetailValue>
                  </DetailItem>
                ) : null}
              </DetailGrid>
            </Card>

            <div style={{ height: '1rem' }} />

            <Card>
              <CardTitle>User</CardTitle>
              <DetailGrid>
                <DetailItem>
                  <DetailLabel>Name</DetailLabel>
                  {user?.id ? (
                    <UserLink
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`${ROUTE_USERS}/${user.id}`);
                      }}
                    >
                      {formatCaseUserName(user)}
                    </UserLink>
                  ) : (
                    <DetailValue>{formatCaseUserName(user)}</DetailValue>
                  )}
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Account status</DetailLabel>
                  <DetailValue>
                    {user?.status ? (
                      <StatusBadge $status={String(user.status)}>
                        {formatUserStatusLabel(user.status)}
                      </StatusBadge>
                    ) : (
                      '—'
                    )}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue $muted={!user?.email}>{user?.email || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>BxLend ID</DetailLabel>
                  <DetailValue $muted={!user?.bxlend_id}>{user?.bxlend_id || '—'}</DetailValue>
                </DetailItem>
              </DetailGrid>
            </Card>

            <div style={{ height: '1rem' }} />

            <Card>
              <CardTitle>Transaction</CardTitle>
              <DetailGrid>
                <DetailItem>
                  <DetailLabel>Type</DetailLabel>
                  <DetailValue>
                    {transaction?.type ? formatFieldString(String(transaction.type)) : '—'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    {transaction?.status ? (
                      <StatusBadge $status={String(transaction.status)}>
                        {formatFieldString(String(transaction.status))}
                      </StatusBadge>
                    ) : (
                      '—'
                    )}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Amount</DetailLabel>
                  <DetailValue>
                    {transaction?.amount
                      ? `${transaction.amount}${transaction.currency_code ? ` ${transaction.currency_code}` : ''}`
                      : '—'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Address</DetailLabel>
                  <CopyableValue value={transaction?.address} label="Address" />
                </DetailItem>
              </DetailGrid>
            </Card>

            {detail?.screening ? (
              <>
                <div style={{ height: '1rem' }} />
                <Card>
                  <CardTitle>Screening</CardTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Risk</DetailLabel>
                      <DetailValue>
                        {detail.screening.risk_level
                          ? formatFieldString(String(detail.screening.risk_level))
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Provider risk</DetailLabel>
                      <DetailValue $muted={!detail.screening.provider_risk}>
                        {detail.screening.provider_risk || '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Recognised</DetailLabel>
                      <DetailValue>
                        {detail.screening.recognised === false ? 'No' : detail.screening.recognised ? 'Yes' : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Reason</DetailLabel>
                      <DetailValue $muted={!detail.screening.reason}>
                        {detail.screening.reason || '—'}
                      </DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Card>
              </>
            ) : null}

            {(detail?.activity?.length ?? 0) > 0 ? (
              <>
                <div style={{ height: '1rem' }} />
                <Card>
                  <CardTitle>Activity</CardTitle>
                  <ActivityList>
                    {detail?.activity?.map((entry, index) => (
                      <ActivityItem key={`${entry.occurred_at}-${index}`}>
                        <DetailValue>
                          {entry.event ? formatFieldString(String(entry.event)) : 'Event'}
                          {entry.occurred_at ? ` · ${convertDate(String(entry.occurred_at)).date}` : ''}
                        </DetailValue>
                        {entry.message ? <MetaLine>{entry.message}</MetaLine> : null}
                      </ActivityItem>
                    ))}
                  </ActivityList>
                </Card>
              </>
            ) : null}

            {isOpen && isScreeningFailed && transaction?.id ? (
              <PermissionGuard permission={PERMISSIONS.TRANSACTION_WRITE}>
                <div style={{ height: '1rem' }} />
                <Card>
                  <CardTitle>Screening</CardTitle>
                  <MetaLine>
                    The provider could not screen this deposit. Retry now instead of waiting for the
                    next automatic attempt; a pass credits the deposit and closes this case.
                  </MetaLine>
                  <ActionRow>
                    <Button
                      text={rescreening ? 'Retrying…' : 'Retry screening'}
                      className="outline"
                      $fullWidth
                      disabled={rescreening || saving}
                      onClick={handleRescreen}
                    />
                  </ActionRow>
                </Card>
              </PermissionGuard>
            ) : null}

            {isOpen ? (
              <PermissionGuard permission={PERMISSIONS.TRANSACTION_WRITE}>
                <div style={{ height: '1rem' }} />
                <Card>
                  <CardTitle>Resolve</CardTitle>
                  <DetailItem>
                    <DetailLabel>Outcome</DetailLabel>
                    <Select
                      options={outcomeOptions}
                      value={outcome}
                      onChange={(e) => setOutcome(e.currentTarget.value)}
                      disabled={saving || outcomeOptions.length === 0}
                    />
                  </DetailItem>
                  <DetailItem style={{ marginTop: '1rem' }}>
                    <DetailLabel>Notes</DetailLabel>
                    <NotesInput
                      placeholder="Optional notes…"
                      value={notes}
                      disabled={saving}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </DetailItem>
                  <ActionRow>
                    <Button
                      text={saving ? 'Submitting…' : 'Submit resolution'}
                      className="black"
                      $fullWidth
                      disabled={saving || !outcome}
                      onClick={handleResolve}
                    />
                  </ActionRow>
                </Card>
              </PermissionGuard>
            ) : null}
          </>
        )}
      </Modal>

      {whitelistPrompt ? (
        <ConfirmModal
          title="Whitelist address?"
          message="Do you want to whitelist this address too? It will skip Chainalysis screening for the configured validity window."
          confirmText="Yes, whitelist"
          cancelText="No"
          onClose={() => {
            setWhitelistPrompt(false);
            onResolved?.();
          }}
          onConfirm={handleWhitelist}
        />
      ) : null}
    </>
  );
};

export default CaseDetailsModal;
